import { baseApi, unwrapEnvelope } from '@/model/client';
import type { ListProjectsResponse } from '@/model/project/dto.generated';
import type { ListWorkspaceMembersResponse } from '@/model/workspace/dto.generated';

export type ProjectListArg = { workspaceId: string; q?: string; limit?: number };

export const PROJECT_PAGE_SIZE = 50;

const NO_CURSOR = '';

const projectEndpoints = baseApi.injectEndpoints({
  endpoints: (build) => ({
    getWorkspaceProjectPages: build.infiniteQuery<ListProjectsResponse, ProjectListArg, string>({
      infiniteQueryOptions: {
        initialPageParam: NO_CURSOR,
        getNextPageParam: (lastPage): string | undefined =>
          lastPage.has_more && lastPage.next_cursor ? lastPage.next_cursor : undefined,
      },
      query: ({ queryArg, pageParam }) => ({
        url: `/api/workspaces/${queryArg.workspaceId}/projects`,
        params: {
          limit: queryArg.limit ?? PROJECT_PAGE_SIZE,
          ...(queryArg.q ? { q: queryArg.q } : {}),
          ...(pageParam === NO_CURSOR ? {} : { cursor: pageParam }),
        },
      }),
      transformResponse: (response: unknown) => unwrapEnvelope<ListProjectsResponse>(response),
      providesTags: ['projects'],
    }),
    getProjectMembers: build.infiniteQuery<
      ListWorkspaceMembersResponse,
      { projectId: string },
      string
    >({
      infiniteQueryOptions: {
        initialPageParam: NO_CURSOR,
        getNextPageParam: (lastPage): string | undefined =>
          lastPage.has_more && lastPage.next_cursor ? lastPage.next_cursor : undefined,
      },
      query: ({ queryArg, pageParam }) => ({
        url: `/api/projects/${queryArg.projectId}/members`,
        ...(pageParam ? { params: { cursor: pageParam } } : {}),
      }),
      transformResponse: (response: unknown) =>
        unwrapEnvelope<ListWorkspaceMembersResponse>(response),
      providesTags: ['project-members'],
    }),
  }),
  overrideExisting: false,
});

export const { useGetWorkspaceProjectPagesInfiniteQuery, useGetProjectMembersInfiniteQuery } =
  projectEndpoints;
